/**
 * Quick Sell coin catalogue (task-list 5.0 T9).
 *
 * Stablecoins are one card each (USDT, USDC) that open a network picker. Each
 * network maps to a backend `coin` name: Tron USDT is the self-custody
 * `usdt`; every other network is a Cobo asset (`usdt-erc20`, `usdc-sol`, …)
 * that behaves like any other coin on the generic coin page.
 *
 * Keep this file byte-identical (bar imports) with
 * expo-app/lib/quick-sell/coinMeta.ts — the unit tests live there.
 */
export type NetworkKey = 'tron' | 'erc20' | 'bsc' | 'sol';

export interface NetworkOption {
  key: NetworkKey;
  label: string;
  /** Backend coin name for this network. */
  coin: string;
  /** Our own wallet (USDT on Tron), not Cobo. */
  selfCustody?: boolean;
  subtitle?: string;
}

export const STABLECOIN_NETWORKS: Record<'usdt' | 'usdc', NetworkOption[]> = {
  usdt: [
    { key: 'tron', label: 'Tron (TRC20)', coin: 'usdt', selfCustody: true },
    { key: 'erc20', label: 'Ethereum (ERC20)', coin: 'usdt-erc20' },
    { key: 'bsc', label: 'BSC (BEP20)', coin: 'usdt-bsc' },
    { key: 'sol', label: 'Solana', coin: 'usdt-sol' },
  ],
  usdc: [
    { key: 'erc20', label: 'Ethereum (ERC20)', coin: 'usdc-erc20' },
    { key: 'bsc', label: 'BSC (BEP20)', coin: 'usdc-bsc' },
    { key: 'sol', label: 'Solana', coin: 'usdc-sol' },
  ],
};

export interface CoinMeta {
  symbol: string;
  label: string;
  /** Network label for stablecoin tokens; undefined for native coins. */
  network?: string;
  /** Coin name to query rates with (`usdt`/`usdc` for tokens). */
  priceCoin: string;
  isStablecoinToken: boolean;
}

const NATIVE_SYMBOLS: Record<string, string> = {
  bitcoin: 'BTC',
  ethereum: 'ETH',
  xrp: 'XRP',
  dogecoin: 'DOGE',
  litecoin: 'LTC',
  tron: 'TRX',
  solana: 'SOL',
  'binance-smart-chain': 'BNB',
  usdt: 'USDT',
  usdc: 'USDC',
};

const NETWORK_SHORT: Record<string, string> = {
  erc20: 'ERC20',
  bsc: 'BEP20',
  sol: 'Solana',
};

const TOKEN_COINS: Record<string, { symbol: 'USDT' | 'USDC'; priceCoin: 'usdt' | 'usdc'; network: NetworkKey }> = {
  'usdt-erc20': { symbol: 'USDT', priceCoin: 'usdt', network: 'erc20' },
  'usdt-bsc': { symbol: 'USDT', priceCoin: 'usdt', network: 'bsc' },
  'usdt-sol': { symbol: 'USDT', priceCoin: 'usdt', network: 'sol' },
  'usdc-erc20': { symbol: 'USDC', priceCoin: 'usdc', network: 'erc20' },
  'usdc-bsc': { symbol: 'USDC', priceCoin: 'usdc', network: 'bsc' },
  'usdc-sol': { symbol: 'USDC', priceCoin: 'usdc', network: 'sol' },
};

export const coinMeta = (coin: string | undefined): CoinMeta => {
  const key = (coin ?? '').toLowerCase();
  const token = TOKEN_COINS[key];
  if (token) {
    const networkLabel = STABLECOIN_NETWORKS[token.priceCoin].find((n) => n.key === token.network)!.label;
    return {
      symbol: token.symbol,
      label: `${token.symbol} (${NETWORK_SHORT[token.network]})`,
      network: networkLabel,
      priceCoin: token.priceCoin,
      isStablecoinToken: true,
    };
  }
  const symbol = NATIVE_SYMBOLS[key] ?? key.toUpperCase();
  // Tron USDT (self-custody) is still "USDT on a network" to the user.
  const network = key === 'usdt' ? STABLECOIN_NETWORKS.usdt[0].label : undefined;
  return { symbol, label: symbol, ...(network ? { network } : {}), priceCoin: key, isStablecoinToken: false };
};

/** 503 { code: 'NETWORK_DISABLED' } from GET/POST deposit-addresses/:coin. */
export const isNetworkDisabledError = (e: unknown): boolean => {
  const err = e as { status?: number; data?: { code?: string } } | undefined;
  return err?.status === 503 && err?.data?.code === 'NETWORK_DISABLED';
};

export const NETWORK_DISABLED_COPY =
  'This network is temporarily unavailable. Please try another network or check back later.';

export const sendOnlyCopy = (symbol: string, network?: string) =>
  `Send only ${symbol}${network ? ` on ${network}` : ''}. Deposits on another network cannot be recovered automatically.`;
